import '../DetailTicketKnowledge.css';
import 'draft-js/dist/Draft.css';
import { Editor, EditorState, ContentState, convertFromHTML } from 'draft-js';
import { useState, useEffect, useRef } from 'react';
import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';
import { MenuItem, TextField, Tooltip } from '@material-ui/core';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { handleOpenFileFromS3 } from '../handler/DetailTicketHandler';
import _ from 'lodash';
import { trLabel } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useOnDemandContentDisplay from '@/component/multiLanguage/hooks/useOnDemandContentDisplay';


const generateBookmarkHTML = (array) => {
    let html = "<ul class='field-bookmark-view-only-wrap'>";
    let currentLevel = 1;
    
    array.forEach(item => {
        if (item.lv > currentLevel) {
            html += "<ul>";
        }
        else if (item.lv < currentLevel) {
            html += "</ul></li>".repeat(currentLevel - item.lv);
        }
        
        html += `<li><span>${item.title}</span>`;
        currentLevel = item.lv;
    });
    
    html += "</ul></li>".repeat(currentLevel - 1);
    
    html += "</ul>";
    return html;
}

function RenderFieldPDFKnowledge({ fieldData, ticketType, onOpenTicket, props }) {
    // Subscribe to translation updates so displayed field labels re-render on language change
    useTranslationSubscription();

    const { displayValue, isShowingTranslation } = useOnDemandContentDisplay(
        fieldData?.dataValue,
        { contentId: props?.ticketId, fieldKey: fieldData?.id }
    );
    const displayFieldData = { ...fieldData, dataValue: displayValue };

    const fieldUI = displayFieldData.structure.UI;
    const isBookmark = displayFieldData?.sysField && displayFieldData.sysField === 'bookmark' && displayFieldData.dataValue;
    const isJSON = Array.isArray(_.attempt(JSON.parse, displayFieldData.dataValue));

    if (isBookmark && !isShowingTranslation && isJSON) {
        const bookmarkData = JSON.parse(displayFieldData.dataValue);
        if (Array.isArray(bookmarkData) && bookmarkData.length > 0) {
            displayFieldData.dataValue = generateBookmarkHTML(bookmarkData);
        }
    }

    switch (fieldUI) {
        case Constant.FIELD_UI.TEXT:
        case Constant.FIELD_UI.NUMBER:
        case Constant.FIELD_UI.DROP_FILE:
            switch (displayFieldData.sysField) {
                case 'parent':
                    return <ParentContentField fieldData={fieldData} ticketType={ticketType} onOpenTicket={onOpenTicket} props={props}/>
                case 'children':
                    return <ChildContentField fieldData={fieldData} ticketType={ticketType} onOpenTicket={onOpenTicket} props={props}/>
                default:
                    return <TextFieldWithTooltip fieldData={displayFieldData} />;
            }
        case Constant.FIELD_UI.FIELD_RICHTEXT:
            return <RichTextViewOnly fieldData={displayFieldData} isBookmark={isBookmark} />
        case Constant.FIELD_UI.URL:
            return <HyperLinkField fieldData={fieldData}/>
        case Constant.FIELD_UI.TAG:
            return (
                <div className="field-pdf-knowledge">
                    <div className="cell-prod-status gap-8">
                        <span className="field-label">{trLabel(displayFieldData?.name)}</span>
                    </div>
                    <TextField
                        className="form-control form-control--wrap form-control--hg32"
                        select
                        value={`${displayFieldData.dataValue}`}
                        disabled={true}
                        variant="outlined"
                        data-cy="header-top-select-project"
                        SelectProps={{
                            MenuProps: {
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                getContentAnchorEl: null,
                                className: 'selectDrop'
                            }
                        }}
                    >
                        <MenuItem value={`${displayFieldData.dataValue}`}>
                            <OverFlowTooltipWithTabsMui
                                className={displayFieldData.id}
                                value={isShowingTranslation ? `${displayFieldData.dataValue}` : displayFieldData.dataValue}
                            >
                                {isShowingTranslation ? `${displayFieldData.dataValue}` : displayFieldData.dataValue}
                            </OverFlowTooltipWithTabsMui>
                        </MenuItem>
                    </TextField>
                </div>
            );
        default:
            return <></>;
    }
}

export default RenderFieldPDFKnowledge;

function HyperLinkField({fieldData}) {
    useTranslationSubscription();
    return (
        <div className='field-pdf-knowledge field-attachment'>
            <span className={`field-label field-attachment-label`}>
                {trLabel(fieldData?.name)}
            </span>
            <span
                className='ticket-redirect'
                onClick={() => handleOpenFileFromS3(fieldData)}
            >
                {fieldData.dataValue.fileName}
            </span>
        </div>
    )
}

function ParentContentField({fieldData, ticketType, onOpenTicket, props}) {
    useTranslationSubscription();
    let parentOutline = fieldData?.dataValue?.outline || Constant.DEFAULT_OUTLINE;
    return (
        <div className='field-pdf-knowledge field-attachment'>
            <span className={`field-label field-attachment-label`}>
                {trLabel(fieldData?.name)}
            </span>
            <span
                className='ticket-redirect'
                onClick={() =>
                    onOpenTicket(
                        fieldData?.dataValue?.id,
                        ticketType,
                        fieldData?.sysField,
                        parentOutline,
                        props?.setIsLoading,
                        props?.currentUserDetail,
                        props?.projectInuseDetail,
                        props?.setIsOpenAccessDeniedPopup,
                        props?.addNewPanel,
                        props?.panelId,
                        props?.isCustomer
                    )
                }
            >
                {parentOutline}
            </span>
        </div>
    )
}

function ChildContentField({ fieldData, ticketType, onOpenTicket, props }) {
    useTranslationSubscription();
    const fieldChildContentRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const element = fieldChildContentRef.current;
            if (element) {
                const scrollRatio = element.scrollTop / (element.scrollHeight - element.clientHeight);
                if (scrollRatio >= 0.95) {
                    // Call api get more
                }
            }
        };

        const element = fieldChildContentRef.current;
        if (element) element.addEventListener('scroll', handleScroll);

        return () => {
            if (element) element.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div className={`field-pdf-knowledge field-attachment ${ticketType != Constant.TICKET_TYPE.PDF_FILE_KNOWLEDGE && 'field-hidden'}`}>
            <span className={`field-label field-attachment-label`}>
                {trLabel(fieldData?.name)}
            </span>
            <div
                className="field-child-content"
                id="field-child-content"
                ref={fieldChildContentRef}
            >
                {!Utils.isEmptyArray(fieldData.dataValue) &&
                    fieldData.dataValue.map((field) => (
                        <span
                            key={field.id}
                            className={`ticket-redirect`}
                            onClick={() => 
                                onOpenTicket(
                                    field.id,
                                    ticketType,
                                    fieldData.sysField,
                                    field.outline,
                                    props?.setIsLoading,
                                    props?.currentUserDetail,
                                    props?.projectInuseDetail,
                                    props?.setIsOpenAccessDeniedPopup,
                                    props?.addNewPanel,
                                    props?.panelId,
                                    props?.isCustomer
                                )}
                        >
                            {field.outline}
                        </span>
                    ))}
            </div>
        </div>
    );
}

function TextFieldWithTooltip({ fieldData }) {
    useTranslationSubscription();
    const textElementRef = useRef();
    const [isOverflowed, setIsOverflowed] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const textElement = textElementRef.current;
            const compare = textElement.scrollWidth > textElement.clientWidth;
            setIsOverflowed(compare);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div className="field-pdf-knowledge">
            <div className="cell-prod-status gap-8">
                <span className="field-label">{trLabel(fieldData?.name)}</span>
            </div>
            <Tooltip title={isOverflowed ? `${fieldData.dataValue}` : ''} placement="top" arrow>
                <input
                    ref={textElementRef}
                    className="form-text-field form-text-field-disable"
                    value={`${fieldData.dataValue}`}
                    disabled={true}
                />
            </Tooltip>
        </div>
    );
}

function RichTextViewOnly({ fieldData, isBookmark }) {
    useTranslationSubscription();
    const [editorState, setEditorState] = useState(EditorState.createEmpty());

    useEffect(() => {
        if (fieldData.dataValue) {
            const blocksFromHtml = convertFromHTML(fieldData.dataValue);
            const contentState = ContentState.createFromBlockArray(
                blocksFromHtml.contentBlocks,
                blocksFromHtml.entityMap
            );
            setEditorState(EditorState.createWithContent(contentState));
        } else {
            setEditorState(EditorState.createEmpty());
        }
    }, [fieldData.dataValue]);

    return (
        <div className={`field-pdf-knowledge`}>
            <div className="cell-prod-status gap-8">
                <span className={`field-label`}>{trLabel(fieldData?.name)}</span>
            </div>
            <div className={'editor editor-custom'} data-cy="richtext-editor">
                {!isBookmark ? (
                    <Editor
                        toolbarOnFocus={true}
                        toolbarClassName={`rich-text__toolbar custom-class`}
                        editorState={editorState}
                        readOnly={true}
                    />
                ) : (
                    <div dangerouslySetInnerHTML={{ __html: fieldData.dataValue }} />
                ) }
            </div>
        </div>
    );
}
